import { forwardRef, type ReactNode } from "react";
import {
  formatOrderSummaryQty,
  type OrderSummaryCardViewModel,
} from "@/modules/chat-tools/domain/build-order-summary-card";
import { cn } from "@/lib/utils";

type OrderSummaryCardProps = {
  summary: OrderSummaryCardViewModel;
  className?: string;
  id?: string;
};

function SummaryPill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded-full bg-secondary px-3 py-1 font-medium text-secondary-foreground text-sm">
      {children}
    </span>
  );
}

function SummaryField({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl bg-muted px-4 py-3 text-base text-foreground leading-relaxed",
        className,
      )}
    >
      {children}
    </div>
  );
}

function SummaryBar({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl bg-secondary px-4 py-2.5 text-center font-medium text-secondary-foreground text-sm">
      {children}
    </div>
  );
}

function TotalRow({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className={strong ? "font-medium" : "text-muted-foreground"}>
        {label}
      </span>
      <span
        className={cn(
          "shrink-0 tabular-nums",
          strong && "font-display text-lg text-secondary",
        )}
      >
        {value}
      </span>
    </div>
  );
}

export const OrderSummaryCard = forwardRef<
  HTMLDivElement,
  OrderSummaryCardProps
>(function OrderSummaryCard({ summary, className, id }, ref) {
  return (
    <div
      ref={ref}
      id={id}
      className={cn(
        "flex w-full max-w-88 flex-col gap-3 rounded-3xl border border-border bg-card p-4 shadow-md print:max-w-[80mm] print:rounded-none print:border-0 print:p-3 print:shadow-none",
        className,
      )}
    >
      <SummaryBar>{summary.shopName}</SummaryBar>

      <div className="flex flex-col gap-1 text-center">
        <p className="font-display text-lg">ສະຫຼຸບອໍເດີ້</p>
        <p className="font-medium text-base tabular-nums tracking-wide">
          {summary.orderNumber}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-start gap-3">
          <SummaryPill>ລູກຄ້າ</SummaryPill>
          <div className="min-w-0 flex-1 pt-0.5">
            <p className="font-medium text-base">{summary.customerName}</p>
            {summary.phone ? (
              <p className="mt-0.5 tabular-nums text-muted-foreground text-sm">
                {summary.phone}
              </p>
            ) : null}
          </div>
        </div>

        <SummaryField className="text-sm">
          <p>{summary.deliverySummary}</p>
          <p className="mt-1 text-muted-foreground">{summary.paymentLabel}</p>
        </SummaryField>

        {summary.address ? (
          <SummaryField className="whitespace-pre-line text-sm">
            {summary.address}
          </SummaryField>
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        <p className="font-medium text-secondary text-sm">ລາຍການສິນຄ້າ</p>
        <div className="flex flex-col gap-1.5">
          {summary.itemGroups.map((group) => {
            const key = group.productName;

            return (
              <div
                key={key}
                className="rounded-xl border border-border/70 bg-muted/50 px-3 py-2 text-sm"
              >
                <p className="font-medium leading-snug">{group.productName}</p>
                <ul className="mt-1.5 flex flex-col gap-1 border-secondary/15 border-l pl-2.5">
                  {group.variants.map((variant) => (
                    <li
                      key={`${variant.variantName}:${variant.isBonus}:${variant.quantity}:${variant.amountLabel}`}
                      className="flex items-start justify-between gap-2"
                    >
                      <span className="min-w-0 text-muted-foreground leading-snug">
                        {variant.variantName}
                        {variant.isBonus ? (
                          <span className="ml-1 text-secondary">[ແຖມ]</span>
                        ) : null}
                      </span>
                      <span className="shrink-0 text-right tabular-nums text-muted-foreground">
                        {formatOrderSummaryQty(
                          variant.quantity,
                          variant.unitLabel,
                        )}{" "}
                        = {variant.amountLabel}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      <SummaryField className="flex flex-col gap-2 py-3">
        <TotalRow label="ຍອດລວມສິນຄ້າ" value={summary.subtotal} />
        {summary.discount ? (
          <TotalRow label="ສ່ວນລົດ" value={summary.discount} />
        ) : null}
        {summary.shipping ? (
          <TotalRow label="ຄ່າສົ່ງ" value={summary.shipping} />
        ) : null}
        <div className="border-secondary/20 border-t pt-2">
          <TotalRow label="ລວມທັງໝົດ" value={summary.total} strong />
        </div>
      </SummaryField>

      {summary.bankLines.length > 0 ? (
        <SummaryField className="whitespace-pre-line text-sm">
          {summary.bankLines.join("\n")}
        </SummaryField>
      ) : null}

      <p className="text-center text-muted-foreground text-xs">
        ຂອບໃຈທີ່ສັ່ງຊື້ 🌻
      </p>
    </div>
  );
});
